import * as React from "react";  
import { format } from "date-fns";  
import { CalendarIcon } from "lucide-react";  
import { cn } from "../../lib/utils";  
import { Button } from "../ui/button";  
import { Calendar } from "../ui/calendar"; // Ensure Calendar component supports month/year navigation  
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";  
import { FormControl } from "../ui/form"; 
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"; 
interface DatePickerProps {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  isFormControl?: boolean;
}

const DatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  className,
  disabled = false,
  error = false,
  isFormControl = false,
}: DatePickerProps) => {
  const [month, setMonth] = React.useState<number>(value ? value.getMonth() : new Date().getMonth());
  const [year, setYear] = React.useState<number>(value ? value.getFullYear() : new Date().getFullYear());
  
  const years = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => currentYear - 99 + i);
  }, []);

  const months = React.useMemo(() => [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ], []);

  React.useEffect(() => {
    if (value) {
      setMonth(value.getMonth());
      setYear(value.getFullYear());
    }
  }, [value]);

  const handleMonthChange = (newMonth: string) => {
    setMonth(months.indexOf(newMonth));
  };

  const handleYearChange = (newYear: string) => {
    setYear(parseInt(newYear, 10));
  };

  const ButtonContent = (
    <Button
      variant="outline"
      className={cn(
        "w-full justify-start text-left",
        !value && "text-muted-foreground",
        error && "border-red-500",
        className
      )}
      disabled={disabled}
      type="button"
    >
      <CalendarIcon className="mr-2 h-4 w-4" />
      {value ? format(value, "PPP") : placeholder}
    </Button>
  );

  const CalendarComponent = (
    <Popover>
      <PopoverTrigger asChild>
        {isFormControl ? <FormControl>{ButtonContent}</FormControl> : ButtonContent}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <Select value={months[month]} onValueChange={handleMonthChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={year.toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[100px]">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            month={new Date(year, month)}
            onMonthChange={(date) => {
              setMonth(date.getMonth());
              setYear(date.getFullYear());
            }}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        </div>
      </PopoverContent>
    </Popover>
  );

  return CalendarComponent;
};

export { DatePicker };